// src/pages/stock/RegistrarStock.js
// Registrar stock asociado a una factura de compra

import React, { useState, useEffect, useMemo } from 'react';
import { Container, Row, Col, Card, Button, Form, Table, Spinner, Alert } from 'react-bootstrap';
import { useNavigate, useSearchParams } from 'react-router-dom';
import NavContainer from '../../components/Nav';
import OffCanvasNav from '../../components/OffCanvasNav';
import { useAuth } from '../../context/AuthContext';
import { createStockReceipt } from '../../services/stockApi';
import { listPurchases } from '../../services/purchaseApi';
import { dotoniwoofApi } from '../../api/dotoniwooApi';

const formatterCOP = new Intl.NumberFormat('es-CO', {
  style: 'currency',
  currency: 'COP',
  minimumFractionDigits: 0,
  maximumFractionDigits: 0
});

const todayYYYYMMDD = () => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
};

const RegistrarStock = () => {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const purchaseFromUrl = searchParams.get('purchase');
  const navigate = useNavigate();

  const [idPurchase, setIdPurchase] = useState(purchaseFromUrl || '');
  const [receiptDate, setReceiptDate] = useState(todayYYYYMMDD());
  const [items, setItems] = useState([]);
  const [purchases, setPurchases] = useState([]);
  const [itemsCatalog, setItemsCatalog] = useState([]);
  const [searchItem, setSearchItem] = useState('');
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [successMsg, setSuccessMsg] = useState('');

  // Nuevo ítem a agregar
  const [selectedItem, setSelectedItem] = useState(null);
  const [newQty, setNewQty] = useState(1);
  const [newUnitCost, setNewUnitCost] = useState('');
  const [newDiscount, setNewDiscount] = useState(0);
  const [newIdPricing, setNewIdPricing] = useState('');
  const [newNotes, setNewNotes] = useState('');

  useEffect(() => {
    const load = async () => {
      const { ok, items: data } = await listPurchases(1, 100);
      if (ok) setPurchases(data);
    };
    load();
  }, []);

  useEffect(() => {
    const load = async () => {
      try {
        const { data } = await dotoniwoofApi.get('/item.php');
        if (data?.code === 200) setItemsCatalog(data.message || []);
      } catch (e) {
        console.error(e);
      }
    };
    load();
  }, []);

  useEffect(() => {
    if (purchaseFromUrl && !idPurchase) setIdPurchase(purchaseFromUrl);
  }, [purchaseFromUrl, idPurchase]);

  const filteredCatalog = useMemo(() => {
    if (!searchItem.trim()) return itemsCatalog.slice(0, 50);
    const term = searchItem.toLowerCase();
    return itemsCatalog.filter(
      (it) =>
        (it.description || '').toLowerCase().includes(term) ||
        (it.product?.name || '').toLowerCase().includes(term) ||
        (it.sku || '').toLowerCase().includes(term)
    ).slice(0, 20);
  }, [itemsCatalog, searchItem]);

  const addItem = () => {
    if (!selectedItem) {
      setErrorMsg('Selecciona un ítem.');
      return;
    }
    const qty = parseInt(newQty, 10);
    const cost = parseFloat(newUnitCost);
    if (!qty || qty < 1) {
      setErrorMsg('La cantidad debe ser al menos 1.');
      return;
    }
    if (isNaN(cost) || cost <= 0) {
      setErrorMsg('El costo unitario debe ser mayor que 0.');
      return;
    }
    setItems((prev) => [
      ...prev,
      {
        key: Date.now(),
        id_item: selectedItem.id_item,
        quantity: qty,
        unit_cost: cost,
        discount_amount: parseFloat(newDiscount) || 0,
        id_pricing: newIdPricing ? parseInt(newIdPricing, 10) : undefined,
        notes: newNotes || undefined,
        _display: selectedItem
      }
    ]);
    setSelectedItem(null);
    setNewQty(1);
    setNewUnitCost('');
    setNewDiscount(0);
    setNewIdPricing('');
    setNewNotes('');
    setErrorMsg('');
  };

  const removeItem = (key) => {
    setItems((prev) => prev.filter((i) => i.key !== key));
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setErrorMsg('');
    setSuccessMsg('');
    if (!user?.uid) {
      setErrorMsg('Sesión no válida.');
      return;
    }
    if (!idPurchase) {
      setErrorMsg('Selecciona una factura de compra.');
      return;
    }
    if (items.length === 0) {
      setErrorMsg('Agrega al menos un ítem.');
      return;
    }
    for (const it of items) {
      if (!it.id_item || !it.quantity || !it.unit_cost) {
        setErrorMsg('Cada ítem debe tener id_item, cantidad y costo unitario.');
        return;
      }
    }
    const payload = {
      firebase_UID: user.uid,
      id_purchase: parseInt(idPurchase, 10),
      receipt_date: receiptDate,
      items: items.map((it) => ({
        id_item: it.id_item,
        quantity: it.quantity,
        unit_cost: it.unit_cost,
        discount_amount: it.discount_amount || 0,
        ...(it.id_pricing ? { id_pricing: it.id_pricing } : {}),
        ...(it.notes ? { notes: it.notes } : {})
      }))
    };
    try {
      setLoading(true);
      const { ok, status, data } = await createStockReceipt(payload);
      if (ok && (status === 201 || data?.code === 201)) {
        setSuccessMsg(data?.message || 'Stock registrado con éxito.');
        setItems([]);
        setTimeout(() => navigate(`/compras/${idPurchase}`), 1500);
      } else {
        setErrorMsg(data?.message || `Error al registrar stock (${status})`);
      }
    } catch (e) {
      setErrorMsg('Error inesperado al enviar.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <NavContainer />
      <OffCanvasNav />
      <Container fluid className="mt-3">
        <Row>
          <Col lg={8}>
            <Card>
              <Card.Header><strong>Registrar stock</strong></Card.Header>
              <Card.Body>
                {errorMsg && <Alert variant="danger" dismissible onClose={() => setErrorMsg('')}>{errorMsg}</Alert>}
                {successMsg && <Alert variant="success" dismissible onClose={() => setSuccessMsg('')}>{successMsg}</Alert>}

                <Form onSubmit={handleSubmit}>
                  <Row className="g-2 mb-3">
                    <Col xs={12} md={6}>
                      <Form.Label>Factura de compra <span className="text-danger">*</span></Form.Label>
                      <Form.Select
                        value={idPurchase}
                        onChange={(e) => setIdPurchase(e.target.value)}
                        required
                      >
                        <option value="">Selecciona factura</option>
                        {purchases.map((p) => (
                          <option key={p.id_purchase} value={p.id_purchase}>
                            #{p.id_purchase} - {p.purchase_date} - {formatterCOP.format(p.grand_total ?? 0)}
                          </option>
                        ))}
                      </Form.Select>
                    </Col>
                    <Col xs={12} md={6}>
                      <Form.Label>Fecha de recepción</Form.Label>
                      <Form.Control type="date" value={receiptDate} onChange={(e) => setReceiptDate(e.target.value)} />
                    </Col>
                  </Row>

                  <hr />
                  <h6 className="mb-2">Agregar ítem</h6>
                  <Row className="g-2 mb-3">
                    <Col xs={12}>
                      <Form.Label>Buscar ítem</Form.Label>
                      <Form.Control
                        placeholder="Buscar por SKU, descripción..."
                        value={searchItem}
                        onChange={(e) => setSearchItem(e.target.value)}
                      />
                    </Col>
                    {filteredCatalog.length > 0 && (
                      <Col xs={12}>
                        <Form.Label>Seleccionar</Form.Label>
                        <Form.Select
                          value={selectedItem?.id_item ?? ''}
                          onChange={(e) => {
                            const id = e.target.value;
                            const it = itemsCatalog.find((i) => String(i.id_item) === id);
                            setSelectedItem(it || null);
                            if (it?.price) setNewUnitCost(String(it.price));
                          }}
                        >
                          <option value="">-- Selecciona ítem --</option>
                          {filteredCatalog.map((it) => (
                            <option key={it.id_item} value={it.id_item}>
                              {it.sku || it.id_item} - {it.description || it.product?.name || '-'}
                            </option>
                          ))}
                        </Form.Select>
                      </Col>
                    )}
                    <Col xs={6} md={2}>
                      <Form.Label>Cantidad</Form.Label>
                      <Form.Control
                        type="number"
                        min="1"
                        value={newQty}
                        onChange={(e) => setNewQty(e.target.value)}
                      />
                    </Col>
                    <Col xs={6} md={2}>
                      <Form.Label>Costo unit.</Form.Label>
                      <Form.Control
                        type="number"
                        step="0.01"
                        min="0"
                        placeholder="0"
                        value={newUnitCost}
                        onChange={(e) => setNewUnitCost(e.target.value)}
                      />
                    </Col>
                    <Col xs={6} md={2}>
                      <Form.Label>Descuento</Form.Label>
                      <Form.Control
                        type="number"
                        step="0.01"
                        min="0"
                        value={newDiscount}
                        onChange={(e) => setNewDiscount(e.target.value)}
                      />
                    </Col>
                    <Col xs={6} md={2}>
                      <Form.Label>id_pricing</Form.Label>
                      <Form.Control
                        type="number"
                        placeholder="Opcional"
                        value={newIdPricing}
                        onChange={(e) => setNewIdPricing(e.target.value)}
                      />
                    </Col>
                    <Col xs={12} md={2} className="d-flex align-items-end">
                      <Button variant="outline-primary" onClick={addItem} className="w-100">
                        Agregar ítem
                      </Button>
                    </Col>
                  </Row>

                  {items.length > 0 && (
                    <>
                      <Table striped bordered size="sm">
                        <thead>
                          <tr>
                            <th>Ítem</th>
                            <th>Cantidad</th>
                            <th>Costo unit.</th>
                            <th>Descuento</th>
                            <th>Total línea</th>
                            <th></th>
                          </tr>
                        </thead>
                        <tbody>
                          {items.map((it) => {
                            const lineTotal = (it.quantity * it.unit_cost) - (it.discount_amount || 0);
                            return (
                              <tr key={it.key}>
                                <td>{it._display?.description || it._display?.sku || it.id_item}</td>
                                <td>{it.quantity}</td>
                                <td>{formatterCOP.format(it.unit_cost)}</td>
                                <td>{formatterCOP.format(it.discount_amount || 0)}</td>
                                <td>{formatterCOP.format(lineTotal)}</td>
                                <td>
                                  <Button variant="outline-danger" size="sm" onClick={() => removeItem(it.key)}>
                                    Quitar
                                  </Button>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </Table>
                      <div className="mt-3">
                        <Button type="submit" variant="primary" disabled={loading}>
                          {loading ? 'Guardando…' : 'Guardar'}
                        </Button>
                        <Button type="button" variant="outline-secondary" className="ms-2" onClick={() => navigate('/compras')}>
                          Cancelar
                        </Button>
                      </div>
                    </>
                  )}
                </Form>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    </>
  );
};

export default RegistrarStock;
